import { z } from 'zod';
import { somarDinheiro, subtrairDinheiro } from './aritmetica.ts';
import { dinheiroSchema, uuidSchema } from './comum.ts';
import { ratearValorPorIndice } from './relatorio-contas-pagar.ts';

/**
 * O que os relatórios de **baixa** compartilham — pagamentos (`PAG·04`) e
 * recebimentos (`REC·04`).
 *
 * Os dois leem a mesma coisa dos dois lados do caixa: a baixa de título, que é
 * o dinheiro que efetivamente saiu ou entrou numa conta corrente num dia. As
 * cinco colunas de valor são as cinco da baixa, e a quebra por data é a data da
 * baixa. O que difere entre eles — a contraparte, a conta contábil do pagar, a
 * obra e o contrato do receber — fica em cada um.
 *
 * É o desenho de `titulos.ts` para os dois módulos de título: subiu para cá o
 * que seria cópia idêntica nos dois, e só isso.
 */

/* -------------------------------------------------------------------------- */
/* As cinco colunas                                                           */
/* -------------------------------------------------------------------------- */

/**
 * Os valores de uma baixa, com os nomes que o relatório imprime.
 *
 * - `bruto` é `valor_baixa_saldo_titulo`: o que a baixa abateu do título.
 * - `desconto`, `juros` e `outros` são as acessórias da baixa.
 * - `liquido` é `valor_liquido`, o que passou pela conta corrente — gravado na
 *   baixa, e não recalculado aqui (ADR 0004): é fato daquela data.
 */
export const valoresDaBaixaSchema = z.object({
  bruto: dinheiroSchema,
  desconto: dinheiroSchema,
  juros: dinheiroSchema,
  outros: dinheiroSchema,
  liquido: dinheiroSchema,
});

export type ValoresDaBaixa = z.infer<typeof valoresDaBaixaSchema>;

/** A soma de um bloco — uma data, uma empresa, uma linha de resumo ou o relatório. */
export const totaisDeBaixasSchema = valoresDaBaixaSchema.extend({
  baixas: z.number().int().nonnegative(),
});

export type TotaisDeBaixas = z.infer<typeof totaisDeBaixasSchema>;

/** As chaves das cinco colunas, na ordem em que o relatório as imprime. */
export const COLUNAS_DE_VALOR_DA_BAIXA = [
  'bruto',
  'desconto',
  'juros',
  'outros',
  'liquido',
] as const;

/**
 * Soma as cinco colunas de uma lista, e conta quantas baixas há nela.
 *
 * `baixas` é passado quando a lista não é de baixas inteiras — as fatias de uma
 * conta contábil contam a baixa uma vez, e não uma vez por fatia.
 */
export function somarValoresDaBaixa(
  lista: readonly ValoresDaBaixa[],
  baixas: number = lista.length,
): TotaisDeBaixas {
  return {
    bruto: somarDinheiro(lista.map((v) => v.bruto)),
    desconto: somarDinheiro(lista.map((v) => v.desconto)),
    juros: somarDinheiro(lista.map((v) => v.juros)),
    outros: somarDinheiro(lista.map((v) => v.outros)),
    liquido: somarDinheiro(lista.map((v) => v.liquido)),
    baixas,
  };
}

function ehZero(valor: string): boolean {
  return /^-?0+(\.0+)?$/.test(valor);
}

/**
 * As cinco colunas de uma baixa divididas pelos índices, **sem perder centavo**.
 *
 * Cada coluna é rateada por si, por maior resto (`ratearValorPorIndice`): a
 * soma das fatias de cada coluna é o valor exato da coluna. O que os índices não
 * cobrem — somam menos de 100% — sai em `resto`, calculado como **o que
 * sobrou**, e não por uma segunda conta de percentual; é o que garante que o
 * resumo fecha no total. `resto` é `null` quando não sobra nada.
 *
 * Sem índice nenhum, a baixa inteira é o resto.
 */
export function ratearValoresDaBaixa(
  valores: ValoresDaBaixa,
  indices: readonly string[],
): { fatias: ValoresDaBaixa[]; resto: ValoresDaBaixa | null } {
  if (indices.length === 0) return { fatias: [], resto: { ...valores } };

  const porColuna = Object.fromEntries(
    COLUNAS_DE_VALOR_DA_BAIXA.map((coluna) => [
      coluna,
      ratearValorPorIndice(valores[coluna], indices),
    ]),
  ) as Record<keyof ValoresDaBaixa, string[]>;

  const fatias = indices.map((_, indice): ValoresDaBaixa => ({
    bruto: porColuna.bruto[indice] as string,
    desconto: porColuna.desconto[indice] as string,
    juros: porColuna.juros[indice] as string,
    outros: porColuna.outros[indice] as string,
    liquido: porColuna.liquido[indice] as string,
  }));

  const resto = Object.fromEntries(
    COLUNAS_DE_VALOR_DA_BAIXA.map((coluna) => [
      coluna,
      subtrairDinheiro(valores[coluna], somarDinheiro(porColuna[coluna])),
    ]),
  ) as ValoresDaBaixa;

  const sobrou = COLUNAS_DE_VALOR_DA_BAIXA.some((coluna) => !ehZero(resto[coluna]));
  return { fatias, resto: sobrou ? resto : null };
}

/* -------------------------------------------------------------------------- */
/* A quebra por data                                                          */
/* -------------------------------------------------------------------------- */

/**
 * Agrupa as linhas por data de baixa, da mais antiga à mais nova, com o
 * subtotal de cada data. A ordem **dentro** da data é de quem chama — cada
 * relatório ordena pela contraparte dele.
 */
export function quebrarPorDataDaBaixa<L extends ValoresDaBaixa>(
  linhas: readonly L[],
  dataDe: (linha: L) => string,
  comparar: (a: L, b: L) => number,
): { data: string; baixas: L[]; totais: TotaisDeBaixas }[] {
  const porData = new Map<string, L[]>();
  for (const linha of linhas) {
    const data = dataDe(linha);
    const doDia = porData.get(data) ?? [];
    doDia.push(linha);
    porData.set(data, doDia);
  }

  return [...porData]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([data, doDia]) => {
      const baixas = [...doDia].sort(comparar);
      return { data, baixas, totais: somarValoresDaBaixa(baixas) };
    });
}

/* -------------------------------------------------------------------------- */
/* O resumo por conta corrente — o mesmo nos dois                             */
/* -------------------------------------------------------------------------- */

/**
 * Uma linha do resumo por conta corrente: o que saiu (ou entrou) em cada conta.
 *
 * A empresa dona da conta vai junto: a descrição é única **por empresa**, e o
 * "Banco do Brasil" de duas empresas do grupo são duas contas.
 */
export const contaCorrenteResumoDeBaixasSchema = totaisDeBaixasSchema.extend({
  contaCorrenteId: uuidSchema,
  contaCorrenteDescricao: z.string(),
  contaCorrenteEmpresaNome: z.string(),
});

export type ContaCorrenteResumoDeBaixas = z.infer<typeof contaCorrenteResumoDeBaixasSchema>;

/** O que o resumo por conta corrente precisa de cada linha. */
export interface BaixaComContaCorrente extends ValoresDaBaixa {
  contaCorrenteId: string;
  contaCorrenteDescricao: string;
  contaCorrenteEmpresaNome: string;
}

export const COMPARAR_TEXTO_BAIXAS = new Intl.Collator('pt-BR', {
  sensitivity: 'base',
  numeric: true,
});

/** A baixa inteira vai à conta dela. Contas pela empresa e depois pela descrição. */
export function resumoPorContaCorrente(
  baixas: readonly BaixaComContaCorrente[],
): ContaCorrenteResumoDeBaixas[] {
  const porConta = new Map<string, BaixaComContaCorrente[]>();
  for (const baixa of baixas) {
    const daConta = porConta.get(baixa.contaCorrenteId) ?? [];
    daConta.push(baixa);
    porConta.set(baixa.contaCorrenteId, daConta);
  }

  return [...porConta]
    .map(([contaCorrenteId, daConta]) => {
      const [primeira] = daConta as [BaixaComContaCorrente];
      return {
        contaCorrenteId,
        contaCorrenteDescricao: primeira.contaCorrenteDescricao,
        contaCorrenteEmpresaNome: primeira.contaCorrenteEmpresaNome,
        ...somarValoresDaBaixa(daConta),
      };
    })
    .sort(
      (a, b) =>
        COMPARAR_TEXTO_BAIXAS.compare(a.contaCorrenteEmpresaNome, b.contaCorrenteEmpresaNome) ||
        COMPARAR_TEXTO_BAIXAS.compare(a.contaCorrenteDescricao, b.contaCorrenteDescricao),
    );
}

/** Os valores da baixa como o repositório os devolve, com os nomes da tabela. */
export interface ValoresBrutosDaBaixa {
  valorBaixaSaldoTitulo: string;
  valorDesconto: string;
  valorJuros: string;
  valorOutros: string;
  valorLiquido: string;
}

/** Da coluna da tabela ao nome do relatório. */
export function valoresDaBaixaBruta(bruta: ValoresBrutosDaBaixa): ValoresDaBaixa {
  return {
    bruto: bruta.valorBaixaSaldoTitulo,
    desconto: bruta.valorDesconto,
    juros: bruta.valorJuros,
    outros: bruta.valorOutros,
    liquido: bruta.valorLiquido,
  };
}
